import { Caption, TextInput, useStyles, pxToRem } from '@archipro-design/aria';
import { TAGGING_SEARCH_MODAL_HEIGHT_PX } from '@modules/product-tagging/constant';
import { Loader, SearchLine } from '@archipro-design/icons';
import type { SyntheticEvent } from 'react';
import React, { useState } from 'react';
import * as S from './ProductTaggingSearchModal.style';
import ProductSearchResultTile from '@modules/product-tagging/components/product-search-result-tile/ProductSearchResultTile';
import { Animation } from '@fluentui/react-northstar';
import {
    URL_SEARCH_PARAM,
    MIN_PROFESSIONAL_SEARCH_CHARACTERS,
    NETWORK_PROFESSIONAL_BLACKLIST,
} from '@modules/product-tagging/config/search-form';
import { useFetcher } from 'react-router';
import { useDebounceFn } from 'ahooks';
import { ProductTaggingSupplierSearch } from '~/routes/remix-api.product-tagging.supplier-search';
import type { TagDataUseToCreateTag } from '@modules/product-tagging/type';
import type { loader } from '~/routes/remix-api.product-tagging.supplier-search';
import { useProductTagging } from '@modules/product-tagging/context';
import {
    TaggingModalShell,
    TaggingModalHeader,
    TaggingModalFieldUnderline,
    TaggingModalContent,
    TaggingModalFooterButton,
    TaggingModalLoadingOverlay,
} from '@modules/product-tagging/components/tagging-modal-shell/TaggingModalShell';

interface SupplierSearchFormProps {
    onClickButton: (e: React.SyntheticEvent<HTMLElement>) => void;
    /** Transitions to the product details form with the selected supplier. */
    onSelectSupplier: (supplier: {
        id: number;
        title: string;
        link: string;
    }) => void;
    /** Called when the user dismisses the modal without submitting. */
    onDismiss?: () => void;
}

export const SupplierSearchForm = ({
    onClickButton,
    onSelectSupplier,
    onDismiss,
}: SupplierSearchFormProps) => {
    const { css } = useStyles();
    const { dispatch, recentlyTaggedProfessionals } = useProductTagging();

    const [keyword, setKeyword] = useState('');

    const fetcher = useFetcher<typeof loader>();

    const { run: searchSupplier } = useDebounceFn(
        (event: SyntheticEvent<HTMLElement, Event>) => {
            const target = event.target as HTMLInputElement;
            setKeyword(target.value);
            if (target.value.length >= MIN_PROFESSIONAL_SEARCH_CHARACTERS) {
                fetcher.load(
                    `${ProductTaggingSupplierSearch}?${URL_SEARCH_PARAM}=${target.value}`,
                );
            }
        },
        {
            wait: 300,
            leading: true,
        },
    );

    const initiateSearch = keyword.length >= MIN_PROFESSIONAL_SEARCH_CHARACTERS;
    const supplierSearchResults = initiateSearch ? fetcher.data || [] : [];

    const excludeBlacklisted = supplierSearchResults.filter(
        (tile) => !NETWORK_PROFESSIONAL_BLACKLIST.includes(tile.id),
    );

    const loading = fetcher.state === 'loading';

    const onClickTile = (data: TagDataUseToCreateTag) => {
        const professional = data.professionalForUnknownProduct;
        if (professional) {
            onSelectSupplier({
                id: professional.id,
                title: professional.title,
                link: professional.link,
            });
        }
    };

    return (
        <TaggingModalShell
            style={{ height: pxToRem(TAGGING_SEARCH_MODAL_HEIGHT_PX) }}
            onDismiss={() => {
                dispatch({ type: 'ADDING_TAG', payload: undefined });
                onDismiss?.();
            }}
        >
            <TaggingModalHeader>
                <div className="w-full">
                    <TextInput
                        className={css(S.SearchTextInput)}
                        input={{
                            placeholder: 'Search product supplier',
                            autoFocus: true,
                        }}
                        size={'md'}
                        suffixIcons={[<SearchLine key={'searchIcon'} />]}
                        onChange={searchSupplier}
                    />
                    <TaggingModalFieldUnderline />
                </div>
            </TaggingModalHeader>

            <TaggingModalContent>
                {excludeBlacklisted.length > 0 ? (
                    <ul aria-label="Search results">
                        {excludeBlacklisted.map((tile) => (
                            <li key={tile.pType + tile.id}>
                                <ProductSearchResultTile
                                    tileData={tile}
                                    showProfessionalName={false}
                                    onConfirm={onClickTile}
                                />
                            </li>
                        ))}
                    </ul>
                ) : (
                    <>
                        {!initiateSearch &&
                        recentlyTaggedProfessionals.length > 0 ? (
                            <>
                                <Caption
                                    className="ml-4 text-gray-500"
                                    variant="04"
                                >
                                    Recently tagged
                                </Caption>
                                <ul aria-label="Recently tagged">
                                    {recentlyTaggedProfessionals.map((tile) => (
                                        <li key={tile.pType + tile.id}>
                                            <ProductSearchResultTile
                                                tileData={tile}
                                                showProfessionalName={false}
                                                onConfirm={onClickTile}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </>
                        ) : (
                            <Caption
                                className="ml-4 text-gray-500"
                                variant="04"
                            >
                                No results
                            </Caption>
                        )}
                    </>
                )}
            </TaggingModalContent>

            <TaggingModalFooterButton
                label="Can't find supplier?"
                onClick={onClickButton}
            />

            {loading && (
                <TaggingModalLoadingOverlay>
                    <Animation
                        name="rotateUltraSlow"
                        className={css(S.TaggingSearchLoader)}
                    >
                        <Loader size="2x" />
                    </Animation>
                </TaggingModalLoadingOverlay>
            )}
        </TaggingModalShell>
    );
};
